import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  writeBatch,
  limit,
} from "firebase/firestore";
import { ref, deleteObject, getBlob, uploadBytes, getDownloadURL } from "firebase/storage";
import { generateThumbnailFilename } from "../files/thumbnailUtils";

export const checkNameExists = async (db, folder, name) => {
  const filesRef = collection(db, "files");
  const foldersRef = collection(db, "folders");

  const qFiles = query(
    filesRef,
    where("folder", "==", folder || ""),
    where("name", "==", name)
  );

  const qFolders = query(
    foldersRef,
    where("folder", "==", folder || ""),
    where("name", "==", name)
  );

  const [filesSnapshot, foldersSnapshot] = await Promise.all([
    getDocs(qFiles),
    getDocs(qFolders),
  ]);

  return !filesSnapshot.empty || !foldersSnapshot.empty;
};

export const deleteFolderRecursively = async (storage, db, folderPath, folderId = null) => {
  try {
    // If explicit folderId is provided, delete it
    if (folderId) {
      try {
        await deleteDoc(doc(db, "folders", folderId));
      } catch (err) {
        console.warn("Failed to delete folder doc by folderId:", folderId, err);
      }
    }

    // Query all files in this folder from Firestore
    const filesCollection = collection(db, "files");
    const qFiles = query(
      filesCollection,
      where("folder", "==", folderPath)
    );
    const filesSnapshot = await getDocs(qFiles);

    // Delete each file in the current folder
    for (const docSnapshot of filesSnapshot.docs) {
      const fileData = docSnapshot.data();
      try {
        // Delete from Firestore
        await deleteDoc(doc(db, "files", docSnapshot.id));

        // Delete from public_shares if exists
        try {
          const qFileShares = query(
            collection(db, "public_shares"),
            where("id", "==", docSnapshot.id)
          );
          const fileSharesSnapshot = await getDocs(qFileShares);
          for (const shareDoc of fileSharesSnapshot.docs) {
            await deleteDoc(doc(db, "public_shares", shareDoc.id));
          }
        } catch (shareErr) {
          console.warn("Failed to delete file public shares:", docSnapshot.id, shareErr);
        }

        // Delete original file from Storage
        if (storage && fileData.fullPath) {
          try {
            const fileRef = ref(storage, fileData.fullPath);
            await deleteObject(fileRef);
          } catch (storageErr) {
            if (storageErr.code !== "storage/object-not-found") {
              console.warn("Failed to delete storage file:", fileData.fullPath, storageErr);
            }
          }
        }

        // Delete thumbnail if exists
        if (storage && fileData.thumbnailUrl) {
          try {
            const thumbnailPath = `thumbnails/${
              fileData.folder ? `${fileData.folder}/` : ""
            }${generateThumbnailFilename(fileData.name)}`;
            const thumbnailRef = ref(storage, thumbnailPath);
            await deleteObject(thumbnailRef);
          } catch (thumbnailError) {
            if (thumbnailError.code !== "storage/object-not-found") {
              console.warn("Failed to delete thumbnail:", thumbnailError);
            }
          }
        }
      } catch (error) {
        console.error("Error deleting file in folder:", fileData.name, error);
      }
    }

    // Query all subfolders in this folder from Firestore
    const foldersCollection = collection(db, "folders");
    const qFolders = query(
      foldersCollection,
      where("folder", "==", folderPath)
    );
    const foldersSnapshot = await getDocs(qFolders);

    // Recursively delete each subfolder
    for (const docSnapshot of foldersSnapshot.docs) {
      const folderData = docSnapshot.data();
      if (folderData.fullPath && folderData.fullPath !== folderPath) {
        await deleteFolderRecursively(storage, db, folderData.fullPath, docSnapshot.id);
      } else {
        // Avoid infinite loop if fullPath equals parent folderPath
        await deleteDoc(doc(db, "folders", docSnapshot.id));
      }
    }

    // After deleting all contents, delete all matching folder documents from Firestore
    if (folderPath) {
      const qFolder = query(
        collection(db, "folders"),
        where("fullPath", "==", folderPath)
      );
      const folderSnapshot = await getDocs(qFolder);
      for (const folderDoc of folderSnapshot.docs) {
        try {
          await deleteDoc(doc(db, "folders", folderDoc.id));
        } catch (err) {
          console.warn("Failed to delete folder doc:", folderDoc.id, err);
        }
      }

      // Also clean up any public shares for this folder
      try {
        const qShares = query(
          collection(db, "public_shares"),
          where("fullPath", "==", folderPath)
        );
        const sharesSnapshot = await getDocs(qShares);
        for (const shareDoc of sharesSnapshot.docs) {
          await deleteDoc(doc(db, "public_shares", shareDoc.id));
        }
      } catch (sharesErr) {
        console.warn("Failed to delete public shares for folder:", folderPath, sharesErr);
      }
    }
  } catch (error) {
    console.error("Error deleting folder:", error);
    throw error;
  }
};

const renameFolderAndContents = async (storage, db, oldFolderPath, newFolderPath, batch) => {
    // Query for subfolders
    const subfoldersQuery = query(collection(db, "folders"), where("folder", "==", oldFolderPath));
    const subfoldersSnapshot = await getDocs(subfoldersQuery);

    for (const folderDoc of subfoldersSnapshot.docs) {
        const folderData = folderDoc.data();
        const newSubfolderPath = `${newFolderPath}/${folderData.name}`;
        await renameFolderAndContents(storage, db, folderData.fullPath, newSubfolderPath, batch);
        const folderRef = doc(db, "folders", folderDoc.id);
        batch.update(folderRef, { folder: newFolderPath, fullPath: newSubfolderPath });
    }

    // Query for files in the current folder
    const filesQuery = query(collection(db, "files"), where("folder", "==", oldFolderPath));
    const filesSnapshot = await getDocs(filesQuery);

    for (const fileDoc of filesSnapshot.docs) {
        const fileData = fileDoc.data();
        const oldFilePath = fileData.fullPath;
        const newFileName = fileData.name;
        const newFilePath = `${newFolderPath}/${newFileName}`;

        // Move file in storage
        const oldFileRef = ref(storage, oldFilePath);
        const newFileRef = ref(storage, newFilePath);
        const blob = await getBlob(oldFileRef);
        await uploadBytes(newFileRef, blob);
        await deleteObject(oldFileRef);

        // Update firestore document
        const newUrl = await getDownloadURL(newFileRef);
        const fileRef = doc(db, "files", fileDoc.id);
        batch.update(fileRef, {
            folder: newFolderPath,
            fullPath: newFilePath,
            originalUrl: newUrl,
        });
    }
};


export const renameFolder = async (storage, db, folder, newName) => {
  const oldPath = folder.fullPath;
  const newPath = folder.folder ? `${folder.folder}/${newName}` : newName;

  const batch = writeBatch(db);

  // Update the folder document itself
  const folderDocRef = doc(db, "folders", folder.id);
  batch.update(folderDocRef, { name: newName, fullPath: newPath });

  await renameFolderAndContents(storage, db, oldPath, newPath, batch);

  await batch.commit();
};

export const moveFile = async (storage, db, file, targetFolder, folderPrefix = "") => {
  if (!file || !file.id) {
    throw new Error("Arquivo inválido para movimentação.");
  }

  // targetFolder pode ser objeto ({ id, fullPath, name }), string de caminho ("Pasta"), ou null / "" (raiz)
  let targetFolderPath = "";
  let targetFolderId = null;
  let targetFolderName = "Meu Drive";

  if (targetFolder) {
    if (typeof targetFolder === "object") {
      targetFolderPath = (
        targetFolder.fullPath ||
        (targetFolder.folder ? `${targetFolder.folder}/${targetFolder.name}` : targetFolder.name) ||
        ""
      ).replace(/^\/+|\/+$/g, "");
      targetFolderId = targetFolder.id || null;
      targetFolderName = targetFolder.name || targetFolderPath || "Meu Drive";
    } else if (typeof targetFolder === "string") {
      targetFolderPath = targetFolder.replace(/^\/+|\/+$/g, "");
      targetFolderName = targetFolderPath.split("/").pop() || "Meu Drive";
    }
  }

  const currentFolderPath = (file.folder || "").replace(/^\/+|\/+$/g, "");
  if (currentFolderPath === targetFolderPath) {
    throw new Error("O arquivo já se encontra nesta pasta.");
  }

  // Se targetFolderPath não for vazio e não tiver targetFolderId, buscar no Firestore
  if (!targetFolderId && targetFolderPath && db) {
    const foldersRef = collection(db, "folders");
    const qFolder = query(foldersRef, where("fullPath", "==", targetFolderPath), limit(1));
    const folderSnap = await getDocs(qFolder);
    if (!folderSnap.empty) {
      targetFolderId = folderSnap.docs[0].id;
      if (!targetFolder || typeof targetFolder !== "object") {
        targetFolderName = folderSnap.docs[0].data()?.name || targetFolderName;
      }
    }
  }

  // Verificar se já existe um arquivo ou pasta com o mesmo nome na pasta de destino
  const nameExists = await checkNameExists(db, targetFolderPath, file.name);
  if (nameExists) {
    throw new Error(`Já existe um arquivo ou pasta com o nome "${file.name}" na pasta de destino.`);
  }

  // Determinar novo caminho no Storage
  const safePrefix = folderPrefix || "";
  const storageFileName = file.fullPath ? file.fullPath.split("/").pop() : file.name;
  let newFilePath;
  if (safePrefix && file.fullPath && file.fullPath.startsWith(safePrefix)) {
    newFilePath = targetFolderPath
      ? `${safePrefix}${targetFolderPath}/${storageFileName}`
      : `${safePrefix}${storageFileName}`;
  } else {
    newFilePath = targetFolderPath
      ? `${targetFolderPath}/${storageFileName}`
      : storageFileName;
  }

  let newUrl = file.originalUrl || null;
  let newThumbnailUrl = file.thumbnailUrl || null;

  // Mover arquivo no Storage se aplicável
  if (storage && file.fullPath && file.fullPath !== newFilePath) {
    try {
      const oldFileRef = ref(storage, file.fullPath);
      const newFileRef = ref(storage, newFilePath);
      const blob = await getBlob(oldFileRef);
      await uploadBytes(newFileRef, blob);
      newUrl = await getDownloadURL(newFileRef);
      await deleteObject(oldFileRef);
    } catch (storageErr) {
      console.warn("Aviso ao mover arquivo no Storage:", storageErr);
    }
  }

  // Mover thumbnail no Storage se existir
  if (storage && file.thumbnailUrl) {
    try {
      const thumbFileName = generateThumbnailFilename(file.name);
      const oldThumbPath = `thumbnails/${currentFolderPath ? `${currentFolderPath}/` : ""}${thumbFileName}`;
      const newThumbPath = `thumbnails/${targetFolderPath ? `${targetFolderPath}/` : ""}${thumbFileName}`;
      if (oldThumbPath !== newThumbPath) {
        const oldThumbRef = ref(storage, oldThumbPath);
        const newThumbRef = ref(storage, newThumbPath);
        const thumbBlob = await getBlob(oldThumbRef);
        await uploadBytes(newThumbRef, thumbBlob);
        newThumbnailUrl = await getDownloadURL(newThumbRef);
        await deleteObject(oldThumbRef);
      }
    } catch (thumbErr) {
      console.warn("Aviso ao mover thumbnail no Storage:", thumbErr);
    }
  }

  // Atualizar referências no Banco de Dados (Firestore) de forma atômica
  const batch = writeBatch(db);

  // 1. Atualizar o documento do arquivo na coleção "files"
  const fileRef = doc(db, "files", file.id);
  const fileUpdates = {
    folder: targetFolderPath,
    immediateParentFolderId: targetFolderId,
    fullPath: newFilePath,
    updatedAt: new Date(),
  };
  if (newUrl) fileUpdates.originalUrl = newUrl;
  if (newThumbnailUrl) fileUpdates.thumbnailUrl = newThumbnailUrl;
  batch.update(fileRef, fileUpdates);

  // 2. Atualizar referências na coleção "public_shares"
  const qShares = query(collection(db, "public_shares"), where("id", "==", file.id));
  const sharesSnapshot = await getDocs(qShares);
  for (const shareDoc of sharesSnapshot.docs) {
    const shareRef = doc(db, "public_shares", shareDoc.id);
    batch.update(shareRef, {
      fullPath: newFilePath,
      updatedAt: new Date(),
    });
  }

  // 3. Atualizar referências de capa de pasta ("folders") se o arquivo era usado como capa
  if (file.thumbnailUrl || file.originalUrl) {
    const urlsToCheck = [file.thumbnailUrl, file.originalUrl].filter(Boolean);
    for (const url of urlsToCheck) {
      const qCovers = query(collection(db, "folders"), where("thumbnailUrl", "==", url));
      const coversSnapshot = await getDocs(qCovers);
      for (const folderDoc of coversSnapshot.docs) {
        const folderDocRef = doc(db, "folders", folderDoc.id);
        batch.update(folderDocRef, {
          thumbnailUrl: newThumbnailUrl || newUrl || url,
          updatedAt: new Date(),
        });
      }
    }
  }

  await batch.commit();

  return {
    success: true,
    targetFolderPath,
    targetFolderId,
    targetFolderName,
    newFilePath,
    newUrl,
    newThumbnailUrl,
  };
};